/* eslint-disable react-refresh/only-export-components */
import { Navigate, Outlet, RouteObject } from "react-router";
import AuthGuard from "./components/AuthGuard";
import DashboardLayout2 from "./components/dashboard/DashboardLayout2";
import GuestGuard from "./components/GuestGuard";
import { Loadable } from "./components/Loadable";
import MainLayout from "./components/MainLayout";
import RoleBasedRedirect from "./components/RoleBasedRedirect";
import EntitlementGuard from "./components/EntitlementGuard";
import lazyWithRetry from "./LazyLoadWithRetry";

// Broadcast pages
const BroadcastDashboardPage = Loadable(
  lazyWithRetry(
    () => import("./pages/communications/broadcast/BroadcastDashboard"),
  ),
);
const BroadcastMessagesPage = Loadable(
  lazyWithRetry(
    () => import("./pages/communications/broadcast/BroadcastMessagesPage"),
  ),
);
const BroadcastTable = Loadable(
  lazyWithRetry(
    () => import("./pages/communications/broadcast/components/BroadcastTable"),
  ),
);
// Legacy broadcast form (deprecated) – kept for reference
// const BroadcastFormPage = Loadable(
//   lazyWithRetry(() => import("./pages/communications/broadcast/BroadcastForm")),
// );
const AudienceBuilderDemoPage = Loadable(
  lazyWithRetry(
    () => import("./pages/communications/broadcast/AudienceBuilderDemo"),
  ),
);
const BroadcastNewPage = Loadable(
  lazyWithRetry(
    () => import("./pages/communications/broadcast/BroadcastNewPage"),
  ),
);

// Authentication pages
const Login = Loadable(
  lazyWithRetry(() => import("./pages/authentication/Login")),
);
const PasswordRecovery = Loadable(
  lazyWithRetry(() => import("./pages/authentication/PasswordRecovery")),
);
const PasswordReset = Loadable(
  lazyWithRetry(() => import("./pages/authentication/PasswordReset")),
);
const Register = Loadable(
  lazyWithRetry(() => import("./pages/authentication/Register")),
);
const VerifyCode = Loadable(
  lazyWithRetry(() => import("./pages/authentication/VerifyCode")),
);

const Overview = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/Overview")),
);

const HealthInformataionSummary = Loadable(
  lazyWithRetry(() => import("./pages/health/HealthInformationSummary")),
);
const MedicalEncounterHome = Loadable(
  lazyWithRetry(() => import("./pages/health/MedicalEncounterHome")),
);

const AiNotesHome = Loadable(
  lazyWithRetry(() => import("./pages/ai-notes/AiNotesHome")),
);

const AiNoteDetail = Loadable(
  lazyWithRetry(() => import("./pages/ai-notes/AiNoteDetail")),
);

const MedicalEncounterPrint = Loadable(
  lazyWithRetry(() => import("./pages/health/encounters/Print/EncounterPrint")),
);

const NotePrint = Loadable(
  lazyWithRetry(
    () =>
      import(
        "./pages/health/encounters/components/Copilot/components/PrintNote"
      ),
  ),
);

const CarePlanHome = Loadable(
  lazyWithRetry(() => import("./pages/health/CarePlanHome")),
);

const MedicationAdministration = Loadable(
  lazyWithRetry(
    () =>
      import(
        "./pages/health/medication-administration/MedicationAdministrationHome"
      ),
  ),
);

const MedicationAdministrationSummary = Loadable(
  lazyWithRetry(
    () =>
      import(
        "./pages/health/medication-administration/MedicationAdministrationSummary"
      ),
  ),
);

const MedicalEncounterSummary = Loadable(
  lazyWithRetry(
    () => import("./pages/health/encounters/MedicalEncounterSummary"),
  ),
);
const PatientAttachmentsPage = Loadable(
  lazyWithRetry(
    () => import("./pages/health/attachments/PatientAttachmentsPage"),
  ),
);
const WrappedEncounterCarePlan = Loadable(
  lazyWithRetry(
    () => import("./pages/health/encounters/WrappedEncounterCarePlan"),
  ),
);
const PrescriptionList = Loadable(
  lazyWithRetry(() => import("./pages/health/prescriptions/PrescriptionList")),
);
const NotificationsPage = Loadable(
  lazyWithRetry(() => import("./pages/notifications/NotificationsPage")),
);
const NotificationDemoPage = Loadable(
  lazyWithRetry(() => import("./pages/notifications/NotificationDemoPage")),
);
const SettingsPage = Loadable(
  lazyWithRetry(() => import("./pages/settings/SettingsPage")),
);

const AppointmentDashboard = Loadable(
  lazyWithRetry(() => import("./pages/appointment/AppointmentDashboard")),
);
const AppointmentFormPage = Loadable(
  lazyWithRetry(() => import("./pages/appointment/AppointmentForm")),
);
const AppointmentListPage = Loadable(
  lazyWithRetry(() => import("./pages/appointment/AppointmentsTable")),
);
const AgendaPrintPreview = Loadable(
  lazyWithRetry(
    () => import("./pages/appointment/components/agenda/AgendaPrintPreview"),
  ),
);

const PatientDashboard = Loadable(
  lazyWithRetry(() => import("./pages/patient/PatientDashboard")),
);
const PatientForm = Loadable(
  lazyWithRetry(() => import("./pages/patient/PatientForm")),
);
const PatientView = Loadable(
  lazyWithRetry(() => import("./pages/patient/PatientView")),
);
const ProviderDashboard = Loadable(
  lazyWithRetry(() => import("./pages/provider/ProviderDashboard")),
);

const PractitionerForm = Loadable(
  lazyWithRetry(() => import("./pages/provider/PractitionerForm")),
);
const PractitionerView = Loadable(
  lazyWithRetry(() => import("./pages/provider/PractitionerView")),
);

const FacilityHome = Loadable(lazyWithRetry(() => import("./pages/facility")));

const FacilityForm = Loadable(
  lazyWithRetry(() => import("./pages/facility/forms/FacilityForm")),
);

const FacilityServiceForm = Loadable(
  lazyWithRetry(() => import("./pages/facility/forms/FacilityServiceForm")),
);

const MedicalConfigurationForm = Loadable(
  lazyWithRetry(
    () => import("./pages/facility/forms/MedicalConfigurationForm"),
  ),
);
const OrgTemplateConfig = Loadable(
  lazyWithRetry(() => import("./pages/facility/OrgTemplateConfig")),
);

// dashboards
const ExecutiveDashboard = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/ExecutiveDashboard")),
);

const TriageDashboard = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/TriageDashboard")),
);

// Admin pages
const AdminProviderEditPage = Loadable(
  lazyWithRetry(() => import("./pages/provider/Admin/AdminProviderEditPage")),
);

// Error pages
const AuthorizationRequired = Loadable(
  lazyWithRetry(() => import("./pages/AuthorizationRequired")),
);
const NotFound = Loadable(lazyWithRetry(() => import("./pages/NotFound")));
const ServerError = Loadable(
  lazyWithRetry(() => import("./pages/ServerError")),
);

type MedialRouteObject = RouteObject & {
  ldFlag?: string;
  ldOrgFlag?: string;
  tier?: string;
  userRole?: string;
};

// facility admin
const FacilityAdminDashboard = Loadable(
  lazyWithRetry(() => import("./pages/facility-admin/FacilityAdminDashBoard")),
);
const LocationFormPage = Loadable(
  lazyWithRetry(
    () => import("./pages/facility-admin/location/LocationFormPage"),
  ),
);

// Organization
const OrganizationDashboard = Loadable(
  lazyWithRetry(() => import("./pages/organization")),
);

// Subscription
const SubscriptionDashboard = Loadable(
  lazyWithRetry(() => import("./pages/subscription")),
);
const BillingHistoryPage = Loadable(
  lazyWithRetry(() => import("./pages/subscription/BillingHistoryPage")),
);

// Invoice
const InvoicePage = Loadable(
  lazyWithRetry(() => import("./pages/invoice/InvoicePage")),
);

// Receipt
const ReceiptPage = Loadable(
  lazyWithRetry(() => import("./pages/invoice/ReceiptPage")),
);

// Inventory & Billing
const BillingSummary = Loadable(
  lazyWithRetry(() => import("./pages/billing/BillingSummary")),
);
const InvoiceForm = Loadable(
  lazyWithRetry(() => import("./pages/billing/InvoiceFormPage")),
);

const ChargeItemDefinitionPage = Loadable(
  lazyWithRetry(() => import("./pages/billing/ChargeItemDefinitionPage")),
);
const ChargeItemDefinitionFormPage = Loadable(
  lazyWithRetry(() => import("./pages/billing/ChargeItemDefinitionFormPage")),
);

const PatientFinancialDetailsPage = Loadable(
  lazyWithRetry(() => import("./pages/billing/PatientFinancialDetailsPage")),
);

const InvoicePrintPage = Loadable(
  lazyWithRetry(() => import("./pages/billing/InvoicePrintPage")),
);

const AccountSummaryPrintPage = Loadable(
  lazyWithRetry(() => import("./pages/billing/AccountSummaryPrintPage")),
);

const LiveFeedsPage = Loadable(
  lazyWithRetry(() => import("./pages/billing/LiveFeedsPage")),
);

const InvoiceActivityPage = Loadable(
  lazyWithRetry(() => import("./pages/billing/InvoiceActivityPage")),
);
const PaymentActivityPage = Loadable(
  lazyWithRetry(() => import("./pages/billing/PaymentActivityPage")),
);

const MessagingActivityPage = Loadable(
  lazyWithRetry(() => import("./pages/billing/MessagingActivityPage")),
);

const PaymentsSettingsPage = Loadable(
  lazyWithRetry(() => import("./pages/billing/stripe/PaymentsSettingsPage")),
);
// TODO: Stephen to implement route filtering using props in MedialRouteObject
const routes: MedialRouteObject[] = [
  {
    path: "invoices/:invoiceId",
    element: (
      <AuthGuard>
        <InvoicePage />
      </AuthGuard>
    ),
  },
  {
    path: "receipts/:receiptId",
    element: (
      <AuthGuard>
        <ReceiptPage />
      </AuthGuard>
    ),
  },
  {
    path: "auth",
    children: [
      { path: "login-unguarded", element: <Login /> },
      { path: "password-recovery", element: <PasswordRecovery /> },
      { path: "password-reset", element: <PasswordReset /> },
      { path: "verify-code", element: <VerifyCode /> },
      {
        path: "login",
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ),
      },
      {
        path: "register",
        element: (
          <GuestGuard>
            <Register />
          </GuestGuard>
        ),
      },
    ],
  },
  {
    path: "home",
    element: (
      <AuthGuard>
        <DashboardLayout2 />
      </AuthGuard>
    ),
    children: [
      {
        path: "summary",
        element: <ExecutiveDashboard />,
      },
      { path: "", element: <Overview /> },
    ],
  },
  {
    path: "health",
    element: (
      <AuthGuard>
        <DashboardLayout2 />
      </AuthGuard>
    ),
    children: [
      {
        path: "summary",
        element: (
          <EntitlementGuard capability="feature_clinical_insights">
            <HealthInformataionSummary />
          </EntitlementGuard>
        ),
      },
      {
        path: "triage",
        element: (
          <EntitlementGuard capability="feature_clinical_triage">
            <TriageDashboard />
          </EntitlementGuard>
        ),
      },
      {
        path: "encounters",
        children: [
          { index: true, element: <MedicalEncounterHome /> },
          {
            path: ":encounterId",
            children: [
              {
                index: true,
                element: <MedicalEncounterSummary interactivity="none" />,
              },
              {
                path: "edit",
                element: <MedicalEncounterSummary interactivity="full" />,
              },
              {
                path: "plan",
                element: <WrappedEncounterCarePlan interactivity="full" />,
              },
              {
                path: "plan/view",
                element: <WrappedEncounterCarePlan interactivity="none" />,
              },
              { path: "print", element: <MedicalEncounterPrint /> },
              {
                path: "printainote",
                element: <NotePrint />,
              },
            ],
          },
        ],
      },

      {
        path: "plans",
        children: [
          {
            path: "",
            element: (
              <EntitlementGuard capability="feature_clinical_care_plans">
                <CarePlanHome />
              </EntitlementGuard>
            ),
          },
        ],
      },

      // prescriptions
      {
        path: "prescriptions",
        element: (
          <EntitlementGuard capability="feature_clinical_prescriptions">
            <PrescriptionList />
          </EntitlementGuard>
        ),
      },

      // medication-administration
      {
        path: "medication-administrations",
        children: [
          {
            path: "",
            element: (
              <EntitlementGuard capability="feature_clinical_med_admin">
                <MedicationAdministration />
              </EntitlementGuard>
            ),
          },
          {
            path: ":encounterId/edit",
            element: (
              <EntitlementGuard capability="feature_clinical_med_admin">
                <MedicationAdministrationSummary interactivity="full" />
              </EntitlementGuard>
            ),
          },
          {
            path: ":encounterId/view",
            element: (
              <EntitlementGuard capability="feature_clinical_med_admin">
                <MedicationAdministrationSummary interactivity="none" />
              </EntitlementGuard>
            ),
          },
        ],
      },
      {
        path: "attachments/:patientId",
        element: (
          <EntitlementGuard capability="feature_patient_mgmt">
            <PatientAttachmentsPage />
          </EntitlementGuard>
        ),
      },
    ],
  },
  {
    path: "patients",
    element: (
      <AuthGuard>
        <DashboardLayout2 />
      </AuthGuard>
    ),
    children: [
      { path: "", element: <PatientDashboard /> },
      { path: "new", element: <PatientForm /> },
      {
        path: ":patientId",
        children: [
          { path: "", element: <PatientView /> },
          { path: "edit", element: <PatientForm /> },
        ],
      },
    ],
  },
  {
    path: "admin",
    element: (
      <AuthGuard>
        <DashboardLayout2 />
      </AuthGuard>
    ),
    children: [{ path: "staff/:id/edit", element: <AdminProviderEditPage /> }],
  },
  {
    path: "staff",
    element: (
      <AuthGuard>
        <DashboardLayout2 />
      </AuthGuard>
    ),
    children: [
      { path: "", element: <ProviderDashboard /> },
      { path: ":practitionerId", element: <PractitionerView /> },
      { path: "profile", element: <PractitionerForm /> },
      { path: ":practitionerId/edit", element: <PractitionerForm /> },
    ],
  },
  {
    path: "practice-info",
    element: (
      <AuthGuard>
        <DashboardLayout2 />
      </AuthGuard>
    ),
    children: [
      { path: "", element: <FacilityHome /> },
      { path: "edit", element: <FacilityForm /> },
      {
        path: "medical-configuration",
        children: [
          { path: "", element: <MedicalConfigurationForm /> },
          {
            path: "templates/:encounterType",
            element: <OrgTemplateConfig />,
          },
          {
            path: "templates/:encounterType/:templateID",
            element: <OrgTemplateConfig />,
          },
        ],
      },
    ],
  },
  {
    path: "facility",
    element: (
      <AuthGuard>
        <DashboardLayout2 />
      </AuthGuard>
    ),
    children: [
      { path: "", element: <FacilityAdminDashboard /> },
      { path: "locations", element: <LocationFormPage /> },
      { path: "locations/:locationId", element: <LocationFormPage /> },
      { path: "services/new", element: <FacilityServiceForm /> },
      { path: "services/:serviceId", element: <FacilityServiceForm /> },
    ],
  },
  {
    path: "communications",
    element: (
      <AuthGuard>
        <EntitlementGuard capability="feature_messaging">
          <DashboardLayout2 />
        </EntitlementGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: "broadcast",
        element: <BroadcastDashboardPage />,
        children: [
          {
            index: true,
            element: <BroadcastTable />,
          },
          {
            path: "messages",
            element: <BroadcastMessagesPage />,
          },
        ],
      },
      {
        path: "broadcast/new",
        element: <BroadcastNewPage />,
      },
      {
        path: "broadcast/:broadcastId",
        element: <AudienceBuilderDemoPage interactivity="none" />,
      },
      {
        path: "broadcast/:broadcastId/edit",
        element: <AudienceBuilderDemoPage interactivity="full" />,
      },
      // Legacy routes (deprecated) – new UI uses same paths above
      // { path: "broadcast/starting-points", element: <BroadcastStartingPointsPage /> },
      // { path: "broadcast/demo", element: <AudienceBuilderDemoPage /> },
      // { path: "broadcast/demo/:broadcastId", element: <AudienceBuilderDemoPage /> },
      // { path: "broadcast/view/:broadcastId", element: <AudienceBuilderDemoPage viewOnly /> },
      // { path: "broadcast/new", element: <BroadcastFormPage interactivity="full" /> },
      // { path: "broadcast/:broadcastId", element: <BroadcastFormPage interactivity="none" /> },
      // { path: "broadcast/:broadcastId/edit", element: <BroadcastFormPage interactivity="full" /> },
    ],
  },
  {
    path: "appointments",
    element: (
      <AuthGuard>
        <EntitlementGuard capability="feature_appointments">
          <DashboardLayout2 />
        </EntitlementGuard>
      </AuthGuard>
    ),
    children: [
      { path: "", element: <AppointmentDashboard /> },
      { path: "new", element: <AppointmentFormPage /> },
      { path: ":appointmentId", element: <AppointmentFormPage /> },
      { path: "search", element: <AppointmentListPage /> },
      { path: "agenda/print", element: <AgendaPrintPreview /> },
    ],
  },
  {
    path: "billing",
    element: (
      <AuthGuard>
        <EntitlementGuard capability="billing_access">
          <DashboardLayout2 />
        </EntitlementGuard>
      </AuthGuard>
    ),
    children: [
      { path: "", element: <BillingSummary /> },
      {
        path: "patient-financials/:accountId",
        element: <PatientFinancialDetailsPage />,
      },
      {
        path: "account-summary/print/:accountId",
        element: <AccountSummaryPrintPage />,
      },
      {
        path: "activities",
        element: <LiveFeedsPage />,
        children: [
          {
            path: "",
            element: <Navigate to="/billing/activities/invoices" replace />,
          },
          { path: "invoices", element: <InvoiceActivityPage /> },
          { path: "payments", element: <PaymentActivityPage /> },
          { path: "messaging", element: <MessagingActivityPage /> },
        ],
      },
      { path: "invoices/form", element: <InvoiceForm /> },
      { path: "invoices/form/:invoiceId", element: <InvoiceForm /> },
      { path: "invoices/print", element: <InvoicePrintPage /> },
      { path: "billable-items", element: <ChargeItemDefinitionPage /> },
      {
        path: "billable-items/form",
        element: <ChargeItemDefinitionFormPage />,
      },
      {
        path: "billable-items/form/:chargeId",
        element: <ChargeItemDefinitionFormPage />,
      },
      { path: "payments", element: <PaymentsSettingsPage /> },
    ],
  },
  {
    path: "notifications",
    element: (
      <AuthGuard>
        <DashboardLayout2 />
      </AuthGuard>
    ),
    children: [
      { path: "", element: <NotificationsPage /> },
      { path: "demo", element: <NotificationDemoPage /> },
    ],
  },
  {
    path: "settings",
    element: (
      <AuthGuard>
        <DashboardLayout2 />
      </AuthGuard>
    ),
    children: [{ path: "", element: <SettingsPage /> }],
  },
  {
    path: "organization",
    element: (
      <AuthGuard>
        <DashboardLayout2 />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <OrganizationDashboard />,
      },
    ],
  },
  {
    path: "subscription",
    element: (
      <AuthGuard>
        <DashboardLayout2 />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <SubscriptionDashboard />,
      },
      {
        path: "billing-history",
        element: <BillingHistoryPage />,
      },
    ],
  },
  {
    path: "ai-notes",
    element: (
      <AuthGuard>
        <DashboardLayout2 />
      </AuthGuard>
    ),
    children: [
      {
        // Single guard for list + detail so nav between them doesn't remount/flash.
        element: (
          <EntitlementGuard capability="ai_scribe" ldFlag="allowAiNotes">
            <Outlet />
          </EntitlementGuard>
        ),
        children: [
          {
            index: true,
            element: <AiNotesHome />,
          },
          {
            path: ":noteId",
            element: <AiNoteDetail />,
          },
        ],
      },
    ],
  },
  {
    path: "/",
    element: (
      <AuthGuard>
        <RoleBasedRedirect />
      </AuthGuard>
    ),
  },
  {
    path: "*",
    element: <MainLayout />,
    children: [
      {
        path: "",
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ),
      },
      { path: "401", element: <AuthorizationRequired /> },
      { path: "404", element: <NotFound /> },
      { path: "500", element: <ServerError /> },
      { path: "*", element: <NotFound /> },
    ],
  },
];

export default routes;
